package uk.co.nickthecoder.kyd.shapes

import uk.co.nickthecoder.glok.property.boilerplate.intProperty
import uk.co.nickthecoder.kyd.util.Vector2

/**
 * A geometric shape, such as a [Line] (a sequence of line segments) or a [Polygon].
 * A [Shape] cannot be drawn directly, that is the job of [ShapeAppearance].
 *
 * Types of shapes to be supported :
 *
 * * Lines (not just simple lines from A to B, but any path with any number of way-points).
 *   The line may intersect itself (which may be very tricky!)
 * * Polygons
 * * Polygons with holes, (the holes are polygons).
 * * Compound Polygon (i.e. two non-overlapping polygons)
 *
 * Shapes that will NOT be supported :
 *
 * * Self-intersecting filled shapes, such as a figure 8.
 *   (You can mimic such a shape by creating a dumbbell shape with 2 holes).
 *
 * Lines and polygons *may* (later) be defined by curves (such as elliptical arcs or bezier curves),
 * but they will ultimately be quantised to a series of straight-line segments / polygons.
 * i.e. the shader never draws curves.
 *
 * Note that [Line] and [Polygon] are separate classes, whereas in other software they may use the same class,
 * with an indication of whether it is open (a line) or closed (a polygon).
 */
abstract class Shape {

    /**
     * Incremented each time the geometry changes.
     * ShapeAppearances can listen to this to invalidate their meshes.
     */
    val updateCounterProperty by intProperty(0)
    var updateCounter by updateCounterProperty


    /**
     * For lines, this will be an empty mesh (with no triangles).
     */
    abstract fun createFillMesh(shapeAppearance: ShapeAppearance): Mesh

    /**
     * For a solid shape with a thickness==0, this will be an empty mesh (with no triangles).
     */
    abstract fun createStrokeMesh(shapeAppearance: ShapeAppearance): Mesh

    abstract fun bounds(): Pair<Vector2, Vector2>

    abstract fun movePoint(index: Int, newPoint: Vector2)

    abstract fun deletePoint(index: Int)

    abstract fun addPoint(index: Int)
}

